<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { useField, useForm } from 'vee-validate'
import { computed } from 'vue'
import * as yup from 'yup'
import { createDiscountAction } from '@/api/functions/settings/discounts-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  modelValue: Boolean,
  currencies: { type: Array, default: () => [] },
  discountTypeOptions: {
    type: Array,
  },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()
const queryClient = useQueryClient()

// Validation schema
const schema = yup.object({
  credit_type: yup.string().required('نوع اعتبار الزامی است'),
  type_id: yup.string().required('نوع تخفیف الزامی است'),
  title: yup.string().required('عنوان تخفیف الزامی است'),
  description: yup.string().required('توضیحات تخفیف الزامی است'),
  currency_id: yup.string().required('ارز موردنظر الزامی است'),
  rate: yup.number().required('مقدار تخفیف الزامی است').typeError('باید عدد باشد'),
  volume_limit: yup.number().required('سقف حجمی تخفیف الزامی است').typeError('باید عدد باشد'),
  expiry_date: yup.string().nullable(),
})

// Form setup
const { handleSubmit, errors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    credit_type: '',
    type_id: '',
    title: '',
    description: '',
    currency_id: '',
    rate: null,
    volume_limit: null,
    expiry_date: null,
  },
})

// Fields
const { value: credit_type } = useField('credit_type')
const { value: type_id } = useField('type_id')
const { value: title } = useField('title')
const { value: description } = useField('description')
const { value: currency_id } = useField('currency_id')
const { value: rate } = useField('rate')
const { value: volume_limit } = useField('volume_limit')
const { value: expiry_date } = useField('expiry_date')

const showDatePicker = computed(() => credit_type.value === 'DURATION_BASED')

const currencyOptions = computed(() => [
  {
    label: 'همه ارزها',
    value: 'all',
  },
  ...(props.currencies ?? []),
])

// Mutation
const createDiscountMutation = useMutationApi({
  mutationFn: (payload: any) => createDiscountAction(payload),
  onSuccess: () => {
    toast.success('تخفیف جدید با موفقیت ایجاد شد', 'موفقیت')
    emit('confirm')
    closeModal()
    // queryClient.invalidateQueries({ queryKey: ['discounts'] });
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Submit
const onSubmit = handleSubmit((values) => {
  createDiscountMutation.mutate(values)
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
    background="#FFF" :closable="true" title="ایجاد تخفیف جدید" closeDirection="left" class="update-request">
    <form @submit.prevent="onSubmit" class="flex flex-col">

      <div class="flex-1">
        <div class="grid px-4 pt-6 pb-9 gap-2">
          <Select v-model="credit_type" label="نوع اعتبار" variant="checkbox" :options="[
            { label: 'یک‌بار مصرف', value: 'ONE_TIME_USE' },
            { label: 'مدت‌دار', value: 'DURATION_BASED' }
          ]" :error="errors.credit_type" />

          <Select v-model="type_id" label="نوع تخفیف" variant="checkbox" :options="discountTypeOptions"
            :error="errors.type_id" />

          <Input v-model="title" name="title" label="عنوان تخفیف" type="text" :error="errors.title" />
          <Input v-model="description" name="description" label="توضیحات تخفیف" type="text"
            :error="errors.description" />
          <Select v-model="currency_id" label="ارز موردنظر" variant="checkbox" :options="currencyOptions"
            :error="errors.currency_id" />
          <Input v-model="rate" name="rate" label="مقدار تخفیف(%)" type="number" :error="errors.rate" />
          <Input v-model="volume_limit" name="volume_limit" label="سقف حجمی تخفیف" type="number"
            :error="errors.volume_limit" />

          <AlirezaDatePicker v-if="showDatePicker" v-model="expiry_date" placeholder="تاریخ اعتبار" class=""
            :error="errors.expiry_date" />
        </div>
      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" label="تایید و ثبت تخفیف" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
          :disabled="createDiscountMutation.isPending.value" :loading="createDiscountMutation.isPending.value" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" :disabled="createDiscountMutation.isPending.value" />
      </div>
    </form>
  </Modal>
</template>
